/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import dayjs from 'dayjs'
import {useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {zodResolver} from '@hookform/resolvers/zod'
import {toast} from 'utils/toastUtils'
import {useTypedSelector} from 'helpers/reducers'
import {useAppDispatch, AppDispatch} from 'helpers/store'
import {CompaniesAction} from 'store/companies/CompaniesAction'
import {ContactsAction} from 'store/contacts/ContactsAction'
import OffersAction from 'store/offers/OffersAction'
import {openSecondaryModal, closeSecondaryModal} from 'helpers/actions'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import {ModalSize} from 'types/enums/ModalSize'
import {Lead} from 'types/entities/Lead'
import {LeadCategory} from 'types/enums/LeadCategory'
import {LeadStatus} from 'types/enums/LeadStatus'
import {CompanyBranchType} from 'types/enums/CompanyBranchType'
import {ReminderModelName} from 'types/enums/ReminderModelName'
import {createAssignmentReminderForEntity} from 'utils/reminders/createAssignmentReminder'
import {useDebouncedCallback} from 'utils/hooks/useDebounce'
import {DEFAULT_HALL_ID} from 'helpers/config'
import {LeadBasicInputs, useLeadBasicSchema} from 'components/leads/schemas/useLeadBasicSchema'
import {sanitizedInputs} from 'utils/utility'
import {Company} from 'types/entities/Company'
import {CompanyContact} from 'types/entities/CompanyContact'
import {CompanyAddress} from 'types/entities/CompanyAddress'
import {useRequiredField} from 'utils/bookingUtils'
import {useLeadContext} from 'utils/hooks/useLeadContext'
import {getEnglishCountryNameFromAnyCountryValue} from 'utils/hooks/useGetCountryCode'
import {
    getAutoBusinessCategoryForBranchType,
    mergeCompanyAfterSave,
    mergeContactAfterSave,
    normalizeCompanyBranchType,
    pruneEmpty,
    useCompanyBranchTypeOptions,
    useGetBusinessCategories,
} from 'utils/companyUtils'
import useMappedSelectOptions from 'components/Companies/hooks/useMappedSelectOptions'
import {applyLeadEventFormDefaults, getLeadFormAddressFields, type LeadFormAddressFields, resolveSchoolTrophyContactPosition} from 'utils/leads/leadEventFormDefaults'
import {normalizeCompanyOrigin} from 'utils/companies/companyOriginUtils'
import {
    formatLeadDateField,
    getDefaultOpportunityCreatedDate,
    isClosingDateEditableStatus,
    mergeLeadAfterSave,
    parseLeadDateField,
} from 'utils/leads/opportunityDateUtils'
import {filteredCountryList, getOrderedLeadCountryOptions, PhoneCountry} from 'utils/countryUtils'
import {europeanCountryCodes} from 'utils/europeanCountries'
import {formatAddressSummary} from 'utils/addressUtils'
import {getPrimaryCompanyContact} from 'utils/companiesUtils'
import {
    buildDraftLeadContact,
    contactSharesEmailDomainWithCompany,
    hasDraftLeadContactFields,
    resolveLeadAccountContactConflict,
} from 'utils/leads/leadAccountContactConflict'
import { extractBusinessEmailDomain } from 'utils/email/emailDomainUtils'
import {
    buildOpportunityName,
    canAutoUpdateOpportunityName,
} from 'utils/leads/opportunityNameUtils'
import LeadAccountContactConflictModal from 'components/leads/LeadAccountContactConflictModal'

export interface UseLeadBasicFormParams {
    lead?: Lead
    company?: Company | null
    predefinedContact?: CompanyContact | null
    onSuccess: (lead: Lead, shouldClose: boolean) => void
    onRequirementsChange?: (requirements: {hasCompany: boolean; hasContact: boolean}) => void
}

export const useLeadBasicForm = ({
    lead: propLead,
    company,
    predefinedContact,
    onSuccess,
    onRequirementsChange,
}: UseLeadBasicFormParams) => {
    const {t} = useTranslation()
    const dispatch = useAppDispatch()
    const {state: leadState, actions: action, isOpportunity} = useLeadContext(propLead?.category)
    const schema = useLeadBasicSchema(isOpportunity)
    const {testField} = useRequiredField(schema)
    const selectedLead = leadState.selectedLead
    const selectedLeadForCurrentEntity =
        selectedLead?.id && propLead?.id && selectedLead.id === propLead.id
            ? selectedLead
            : null

    const effectiveLead = selectedLeadForCurrentEntity || propLead
    const leadFormSyncToken = useMemo(() => {
        if (!effectiveLead?.id) {
            return null
        }

        return [
            effectiveLead.id,
            effectiveLead.updated_at ?? '',
            effectiveLead.contact_id ?? '',
            effectiveLead.company_id ?? '',
        ].join(':')
    }, [effectiveLead?.id, effectiveLead?.updated_at, effectiveLead?.contact_id, effectiveLead?.company_id])
    const session = useTypedSelector((state) => state.sessionState.session)
    const seller_id = session?.uid
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [selectedCategory, setSelectedCategory] = useState<string | undefined>(effectiveLead?.category)

    const [selectedCompany, setSelectedCompany] = useState<Company | null>(company || effectiveLead?.company || null)
    const [selectedContact, setSelectedContact] = useState<CompanyContact | null>(null)
    const [selectedContactCountry, setSelectedContactCountry] = useState<PhoneCountry>(
        filteredCountryList(europeanCountryCodes)[30]
    )
    const [companyContacts, setCompanyContacts] = useState<CompanyContact[]>(
        company?.contacts || effectiveLead?.company?.contacts || []
    )
    const [selectedCompanyContactId, setSelectedCompanyContactId] = useState<number | null>(
        effectiveLead?.contact_id ||
            effectiveLead?.company?.default_contact_id ||
            effectiveLead?.company?.contacts?.[0]?.id ||
            company?.default_contact_id ||
            company?.contacts?.[0]?.id ||
            null
    )
    const [companyAddresses, setCompanyAddresses] = useState<CompanyAddress[]>(
        company?.addresses || effectiveLead?.company?.addresses || []
    )
    const [selectedCompanyAddressId, setSelectedCompanyAddressId] = useState<number | null>(
        effectiveLead?.company?.default_address_id ||
            effectiveLead?.company?.addresses?.[0]?.id ||
            company?.default_address_id ||
            company?.addresses?.[0]?.id ||
            null
    )
    const [isCreatingNewAddress, setIsCreatingNewAddress] = useState<boolean>(
        !(effectiveLead?.company?.addresses?.length || company?.addresses?.length)
    )
    const [companyInputValue, setCompanyInputValue] = useState('')
    const [companyAutocompleteKey, setCompanyAutocompleteKey] = useState(0)
    const selectedContactRef = useRef<CompanyContact | null>(null)
    const selectedCompanyRef = useRef<Company | null>(null)
    const selectedCompanyContactIdRef = useRef<number | null>(null)
    selectedContactRef.current = selectedContact
    selectedCompanyRef.current = selectedCompany
    selectedCompanyContactIdRef.current = selectedCompanyContactId
    const {options: companyTypeOptions} = useCompanyBranchTypeOptions()
    const {businessCategories} = useGetBusinessCategories()
    const companyTypeOptionsUi = useMappedSelectOptions(companyTypeOptions)
    const businessCategoryOptions = useMappedSelectOptions(businessCategories)
    const [tags, setTags] = useState<string[]>(
        Array.isArray(effectiveLead?.tags)
            ? effectiveLead.tags.filter((tag) => typeof tag === 'string' && tag.trim() !== '')
            : []
    )
    const shouldCheckDuplicatesRef = useRef(false)
    const autoSaveOnBlurRef = useRef({company: false})
    const addressExplicitlyChangedRef = useRef(false)
    const accountTypeAutoCategoryMountedRef = useRef(false)
    const initialAutoOpportunityName = buildOpportunityName(
        effectiveLead?.anlasstyp || '',
        effectiveLead?.company_name || company?.name || ''
    )
    const lastAutoOpportunityNameRef = useRef(
        initialAutoOpportunityName &&
            (effectiveLead?.name || '').trim() === initialAutoOpportunityName
            ? initialAutoOpportunityName
            : ''
    )

    const initialCompany = company || effectiveLead?.company || null
    const initialAddressId =
        effectiveLead?.company?.default_address_id ||
        effectiveLead?.company?.addresses?.[0]?.id ||
        company?.default_address_id ||
        company?.addresses?.[0]?.id ||
        null
    const initialAddressFields = getLeadFormAddressFields(initialCompany, initialAddressId)

    const form = useForm<LeadBasicInputs>({
        resolver: zodResolver(schema),
        defaultValues: applyLeadEventFormDefaults(effectiveLead, {
            name: effectiveLead?.name || '',
            company_name: effectiveLead?.company_name || company?.name || '',
            anlasstyp: effectiveLead?.anlasstyp || '',
            first_name: effectiveLead?.contact?.first_name || '',
            title: effectiveLead?.contact?.title || '',
            custom_title: effectiveLead?.contact?.custom_title || '',
            position: effectiveLead?.contact?.position || '',
            last_name: effectiveLead?.contact?.last_name || '',
            gender: effectiveLead?.contact?.gender || undefined,
            contact_origin: effectiveLead?.contact?.origin || '',
            contact_note: effectiveLead?.contact?.note || '',
            company_type: normalizeCompanyBranchType(effectiveLead?.company?.type),
            business_category: effectiveLead?.company?.business_category || '',
            company_origin: effectiveLead?.company?.origin || '',
            email: effectiveLead?.email || '',
            contact_email: effectiveLead?.contact?.email || effectiveLead?.email || '',
            contact_phone: effectiveLead?.contact?.phone || effectiveLead?.phone || '',
            website: effectiveLead?.website || '',
            source: effectiveLead?.source || '',
            referral_id:
                effectiveLead?.referral_id != null
                    ? String(effectiveLead.referral_id)
                    : effectiveLead?.company?.referral_id != null
                      ? String(effectiveLead.company.referral_id)
                      : company?.referral_id != null
                        ? String(company.referral_id)
                        : '',
            tags: Array.isArray(effectiveLead?.tags)
                ? effectiveLead.tags.filter((tag: string) => typeof tag === 'string' && tag.trim() !== '')
                : [],
            street_address: initialAddressFields.street_address,
            city: initialAddressFields.city,
            postal_code: initialAddressFields.postal_code,
            state: initialAddressFields.state,
            country: initialAddressFields.country,
            requested_date: effectiveLead?.requested_date
                ? dayjs(effectiveLead.requested_date).toDate()
                : undefined,
            created_date:
                parseLeadDateField(effectiveLead?.created_date) ??
                (isOpportunity && !effectiveLead?.id ? getDefaultOpportunityCreatedDate() : undefined),
            closed_at: parseLeadDateField(effectiveLead?.closed_at),
            category: (effectiveLead?.category as LeadCategory) || LeadCategory.Cold,
            is_new_contact: false,
            seller_id: effectiveLead?.seller_id || seller_id || undefined,
        }),
    })

    const {register, handleSubmit, control, formState: {errors, dirtyFields}, setValue, setError, reset, getValues, watch} = form

    const companyNameInput = watch('company_name')
    const anlasstypInput = watch('anlasstyp')
    const companyTypeInput = watch('company_type')
    const countryInput = watch('country')
    const emailInput = watch('email')
    const titleInput = watch('title')
    const positionInput = watch('position')
    const genderInput = watch('gender')
    const contactEmailInput = watch('contact_email')
    const contactPhoneInput = watch('contact_phone')
    const firstNameInput = watch('first_name')
    const lastNameInput = watch('last_name')
    const streetAddressInput = watch('street_address')
    const postalCodeInput = watch('postal_code')
    const cityInput = watch('city')

    const hasCompany = Boolean(selectedCompany?.id || companyInputValue?.trim())
    const hasContact = Boolean(
        selectedContact?.id ||
            titleInput?.trim() ||
            positionInput?.trim() ||
            firstNameInput?.trim() ||
            lastNameInput?.trim() ||
            contactEmailInput?.trim() ||
            contactPhoneInput?.trim()
    )
    const isNewContact = !selectedContact && Boolean(firstNameInput?.trim() || lastNameInput?.trim())

    const syncAutoOpportunityName = useCallback(
        (anlasstyp: string | null | undefined, accountName: string | null | undefined) => {
            if (!isOpportunity) {
                return
            }

            const nextName = buildOpportunityName(anlasstyp, accountName)
            if (!nextName) {
                return
            }

            const currentName = (getValues('name') ?? '').trim()
            if (currentName === nextName) {
                lastAutoOpportunityNameRef.current = nextName
                return
            }

            if (!canAutoUpdateOpportunityName(currentName, lastAutoOpportunityNameRef.current)) {
                return
            }

            lastAutoOpportunityNameRef.current = nextName
            setValue('name', nextName, {shouldDirty: true, shouldValidate: true})
        },
        [getValues, isOpportunity, setValue]
    )

    useEffect(() => {
        if (!isOpportunity) {
            return
        }
        syncAutoOpportunityName(anlasstypInput, companyNameInput)
    }, [anlasstypInput, companyNameInput, isOpportunity, syncAutoOpportunityName])

    const checkDuplicates = useDebouncedCallback(
        async (name?: string, companyName?: string, email?: string, isManual?: boolean) => {
            const hasEnoughInfo =
                (name && name.length > 2) || (companyName && companyName.length > 2) || (email && email.length > 2)
            if (hasEnoughInfo) {
                try {
                    const results = await dispatch(
                        CompaniesAction.fetchCheckDuplicates({name: companyName, email})
                    )
                    if (results.length > 0 && isManual) {
                        toast.warning(t('A company with this name or email already exists'))
                    }
                } catch (e) {
                    console.error('Failed to check duplicates', e)
                }
            }
        },
        500
    )

    const orderedCountryOptions = useMemo(() => getOrderedLeadCountryOptions(europeanCountryCodes), [])

    const findCountryOption = useCallback(
        (countryValue?: string) => {
            const normalizedValue = (countryValue || '').toString().trim().toLowerCase()
            if (!normalizedValue) return null
            const swissAliases = ['schweiz', 'suisse', 'svajciarsko', 'switzerland']
            if (swissAliases.includes(normalizedValue)) {
                return orderedCountryOptions.find((option) => option.textCode === 'CH') || null
            }
            return (
                orderedCountryOptions.find(
                    (option) =>
                        option.textCode.toLowerCase() === normalizedValue ||
                        option.name.toLowerCase() === normalizedValue
                ) || null
            )
        },
        [orderedCountryOptions]
    )

    const applyAddressFromCompanyAddress = useCallback(
        (address: CompanyAddress | null) => {
            if (!address) return
            setValue('street_address', address.street_name || '', {shouldDirty: true})
            setValue('city', address.town || '', {shouldDirty: true})
            setValue('postal_code', address.postal_code || '', {shouldDirty: true})
            setValue('state', address.region || '', {shouldDirty: true})
            setValue('country', address.country || 'Switzerland', {shouldDirty: true})
        },
        [setValue]
    )

    const clearAddressFields = useCallback(() => {
        setValue('street_address', '', {shouldDirty: true})
        setValue('city', '', {shouldDirty: true})
        setValue('postal_code', '', {shouldDirty: true})
        setValue('state', '', {shouldDirty: true})
        setValue('country', 'Switzerland', {shouldDirty: true})
    }, [setValue])

    const handleContactSelect = useCallback(
        (contact: CompanyContact | null) => {
            setSelectedContact(contact)
            if (contact) {
                setSelectedCompanyContactId(contact.id)
                setValue('title', contact.title || '', {shouldDirty: true})
                setValue('custom_title', contact.custom_title || '', {shouldDirty: true})
                setValue(
                    'position',
                    resolveSchoolTrophyContactPosition(effectiveLead, contact.position) || '',
                    {shouldDirty: true}
                )
                setValue('first_name', contact.first_name || '', {shouldDirty: true})
                setValue('last_name', contact.last_name || '', {shouldDirty: true})
                setValue('gender', contact.gender || undefined, {shouldDirty: true})
                setValue('contact_email', contact.email || '', {shouldDirty: true})
                setValue('contact_phone', contact.phone || '', {shouldDirty: true})
                setValue('contact_origin', contact.origin || '', {shouldDirty: true})
                setValue('contact_note', contact.note || '', {shouldDirty: true})
            } else {
                setSelectedCompanyContactId(null)
                setValue('title', '', {shouldDirty: true})
                setValue('custom_title', '', {shouldDirty: true})
                setValue('position', '', {shouldDirty: true})
                setValue('first_name', '', {shouldDirty: true})
                setValue('last_name', '', {shouldDirty: true})
                setValue('gender', undefined, {shouldDirty: true})
                setValue('contact_email', '', {shouldDirty: true})
                setValue('contact_phone', '', {shouldDirty: true})
                setValue('contact_origin', '', {shouldDirty: true})
                setValue('contact_note', '', {shouldDirty: true})
            }
        },
        [effectiveLead, setValue]
    )

    const applyCompanyAddresses = useCallback(
        (companyToApply: Company) => {
            const addresses = companyToApply.addresses || []
            setCompanyAddresses(addresses)

            if (addresses.length === 1) {
                setSelectedCompanyAddressId(addresses[0].id)
                setIsCreatingNewAddress(false)
                applyAddressFromCompanyAddress(addresses[0])
            } else if (addresses.length > 1) {
                const selectedAddress =
                    addresses.find((a) => a.id === companyToApply.default_address_id) || addresses[0]
                setSelectedCompanyAddressId(selectedAddress?.id || null)
                setIsCreatingNewAddress(false)
                applyAddressFromCompanyAddress(selectedAddress || null)
            } else {
                setSelectedCompanyAddressId(null)
                setIsCreatingNewAddress(true)
                clearAddressFields()
            }
        },
        [applyAddressFromCompanyAddress, clearAddressFields]
    )

    const applyCompanyBaseFields = useCallback(
        (companyToApply: Company, shouldAutoSave: boolean) => {
            setSelectedCompany(companyToApply)
            autoSaveOnBlurRef.current.company = shouldAutoSave && Boolean(companyToApply.id)
            setCompanyContacts(companyToApply.contacts || [])
            setCompanyInputValue(companyToApply.name || '')
            setValue('company_name', companyToApply.name || '')
            if (companyToApply.email && !getValues('email')) setValue('email', companyToApply.email)
            if (companyToApply.phone && !getValues('phone')) setValue('phone', companyToApply.phone)
            if (companyToApply.website && !getValues('website')) setValue('website', companyToApply.website)
            setValue(
                'company_type',
                normalizeCompanyBranchType(companyToApply.type) || CompanyBranchType.Company,
                {shouldDirty: true}
            )
            setValue('business_category', companyToApply.business_category || '', {shouldDirty: true})
            setValue('company_origin', companyToApply.origin || '', {shouldDirty: true})
            if (companyToApply.referral_id != null && !getValues('referral_id')) {
                setValue('referral_id', String(companyToApply.referral_id), {shouldDirty: true})
            }
            applyCompanyAddresses(companyToApply)
        },
        [applyCompanyAddresses, getValues, setValue]
    )

    const applyPrimaryContactForCompany = useCallback(
        (companyToApply: Company) => {
            const primaryContact = getPrimaryCompanyContact(companyToApply)
            handleContactSelect(primaryContact ?? null)
        },
        [handleContactSelect]
    )

    const applyCompanyKeepingContact = useCallback(
        (companyToApply: Company, contact: CompanyContact, shouldAutoSave: boolean) => {
            applyCompanyBaseFields(companyToApply, shouldAutoSave)
            const contacts = companyToApply.contacts || []
            if (!contacts.some((c) => c.id === contact.id)) {
                setCompanyContacts([...contacts, contact])
            }
            handleContactSelect(contact)
        },
        [applyCompanyBaseFields, handleContactSelect]
    )

    const handleCompanySelect = useCallback(
        async (companyArg: Company | null, shouldAutoSave: boolean = true) => {
            let companyToApply = companyArg
            if (companyArg?.id) {
                try {
                    companyToApply = await dispatch(CompaniesAction.fetchCompanyByIdRaw(companyArg.id))
                } catch {
                    companyToApply = companyArg
                }
            }

            if (!companyToApply) {
                setSelectedCompany(null)
                autoSaveOnBlurRef.current.company = false
                setCompanyContacts([])
                setSelectedCompanyContactId(null)
                setCompanyAddresses([])
                setSelectedCompanyAddressId(null)
                setIsCreatingNewAddress(true)
                setCompanyInputValue('')
                setValue('company_type', CompanyBranchType.Company, {shouldDirty: true})
                setValue('business_category', '', {shouldDirty: true})
                setValue('company_origin', '', {shouldDirty: true})
                clearAddressFields()
                return
            }

            const formValues = getValues()
            const draftFields = {
                first_name: formValues.first_name,
                last_name: formValues.last_name,
                title: formValues.title,
                custom_title: formValues.custom_title,
                position: formValues.position,
                gender: formValues.gender,
                contact_email: formValues.contact_email,
                contact_phone: formValues.contact_phone,
                contact_origin: formValues.contact_origin,
                contact_note: formValues.contact_note,
            }
            const hasDraftFields = hasDraftLeadContactFields(draftFields)

            let currentContact = selectedContactRef.current
            if (!currentContact?.id && selectedCompanyContactIdRef.current) {
                currentContact =
                    companyContacts.find((c) => c.id === selectedCompanyContactIdRef.current) ||
                    currentContact
            }
            if (!currentContact?.id && hasDraftFields) {
                currentContact = buildDraftLeadContact(draftFields)
            }

            const decision = resolveLeadAccountContactConflict(currentContact, companyToApply, {
                hasDraftContactFields: hasDraftFields,
            })

            if (decision === 'conflict' && currentContact) {
                const previousCompany = selectedCompanyRef.current
                const primaryContact = getPrimaryCompanyContact(companyToApply) ?? null
                const canAssign = Boolean(currentContact.id)
                const sharesDomain = contactSharesEmailDomainWithCompany(
                    currentContact,
                    companyToApply
                )
                const matchedDomain = sharesDomain
                    ? extractBusinessEmailDomain(currentContact.email)
                    : null

                dispatch(
                    openSecondaryModal(
                        <LeadAccountContactConflictModal
                            contact={currentContact}
                            primaryContact={primaryContact}
                            canAssign={canAssign}
                            emailDomainMatch={matchedDomain}
                            onAssign={async () => {
                                try {
                                    if (canAssign) {
                                        await dispatch(
                                            ContactsAction.fetchAssignToCompany(
                                                currentContact.id,
                                                companyToApply
                                            )
                                        )
                                        applyCompanyKeepingContact(
                                            companyToApply,
                                            currentContact,
                                            shouldAutoSave
                                        )
                                    } else {
                                        // Keep draft form fields; only apply Account.
                                        applyCompanyBaseFields(companyToApply, shouldAutoSave)
                                    }
                                    dispatch(closeSecondaryModal())
                                } catch {
                                    toast.error(t('Failed to assign contact to company'))
                                }
                            }}
                            onReplace={() => {
                                applyCompanyBaseFields(companyToApply, shouldAutoSave)
                                applyPrimaryContactForCompany(companyToApply)
                                dispatch(closeSecondaryModal())
                            }}
                            onCancel={() => {
                                dispatch(closeSecondaryModal())
                                setSelectedCompany(previousCompany)
                                setCompanyInputValue(previousCompany?.name || '')
                                setCompanyAutocompleteKey((key) => key + 1)
                            }}
                        />,
                        {modalSize: ModalSize.Small},
                        t('Account Contact not linked')
                    )
                )
                return
            }

            if (decision === 'keep_contact' && currentContact?.id) {
                applyCompanyKeepingContact(companyToApply, currentContact, shouldAutoSave)
                return
            }

            applyCompanyBaseFields(companyToApply, shouldAutoSave)
            applyPrimaryContactForCompany(companyToApply)
        },
        [
            applyCompanyBaseFields,
            applyCompanyKeepingContact,
            applyPrimaryContactForCompany,
            clearAddressFields,
            companyContacts,
            dispatch,
            getValues,
            setValue,
            t,
        ]
    )

    const handleCompanyNameChange = useCallback(
        (value: string) => {
            setCompanyInputValue(value)
            shouldCheckDuplicatesRef.current = true
            autoSaveOnBlurRef.current.company = false
            setValue('company_name', value, {shouldDirty: true})
        },
        [setValue]
    )

    const handleCreateCompanyFromLead = useCallback(
        (query: string) => {
            setSelectedCompany(null)
            setCompanyAddresses([])
            setSelectedCompanyAddressId(null)
            setIsCreatingNewAddress(true)
            shouldCheckDuplicatesRef.current = true
            autoSaveOnBlurRef.current.company = true
            if (query) {
                setCompanyInputValue(query)
                setValue('company_name', query, {shouldDirty: true})
            } else {
                setCompanyInputValue('')
            }
        },
        [setValue]
    )

    const handleAutoSaveBlur = useCallback(
        (source: 'company') => {
            if (!autoSaveOnBlurRef.current[source]) return
            if (!effectiveLead?.id) {
                autoSaveOnBlurRef.current[source] = false
                return
            }
            if (isSubmitting) return
            autoSaveOnBlurRef.current[source] = false
            handleSubmit((data) => onSubmit(data, false), onFormError)()
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [effectiveLead?.id, isSubmitting, handleSubmit]
    )

    const handleCompanyAddressSelect = useCallback(
        (addressId: string | number | null) => {
            addressExplicitlyChangedRef.current = true
            const nextId = addressId ? Number(addressId) : null
            setSelectedCompanyAddressId(nextId)
            setIsCreatingNewAddress(false)
            const selectedAddress = companyAddresses.find((address) => address.id === nextId) || null
            applyAddressFromCompanyAddress(selectedAddress)
        },
        [companyAddresses, applyAddressFromCompanyAddress]
    )

    const handleCreateNewAddress = useCallback(() => {
        addressExplicitlyChangedRef.current = true
        setIsCreatingNewAddress(true)
        setSelectedCompanyAddressId(null)
        clearAddressFields()
    }, [clearAddressFields])

    const getSubmitPayload = useCallback(
        (data: LeadBasicInputs): Partial<Lead> => {
            const payload: Record<string, unknown> = {
                name: data.name?.toString().trim(),
                company_name: (data.company_name || '')?.toString().trim(),
                email: data.email?.toString().trim(),
                phone: data.phone?.toString().trim(),
                website: data.website?.toString().trim(),
                category: (selectedCategory as LeadCategory) ?? LeadCategory.Warm,
                status: effectiveLead?.status ?? (isOpportunity ? LeadStatus.NewInquiry : LeadStatus.New),
                source: data.source?.toString().trim() || null,
                referral_id: data.referral_id?.toString().trim() || null,
                requested_date: data.requested_date ? dayjs(data.requested_date).format('YYYY-MM-DD') : undefined,
                requested_time: data.requested_date ? dayjs(data.requested_date).format('HH:mm') : undefined,
                persons: data.persons || 0,
                tags: tags,
                id: effectiveLead?.id || null,
                hall_id: effectiveLead?.hall_id || DEFAULT_HALL_ID,
            }
            if (isOpportunity) {
                payload.anlasstyp = data.anlasstyp?.toString().trim() || null
                payload.created_date = formatLeadDateField(data.created_date ?? null)

                const leadStatus = effectiveLead?.status
                if (isClosingDateEditableStatus(leadStatus)) {
                    payload.closed_at = formatLeadDateField(data.closed_at ?? null)
                } else {
                    payload.closed_at = null
                }
            }
            const fullName = [data.first_name, data.last_name]
                .map((v) => (v || '').toString().trim())
                .filter(Boolean)
                .join(' ')
            if (fullName) {
                payload.contact_person = fullName
                payload.full_name = fullName
            }
            const resolvedSellerId =
                (data.seller_id ? Number(data.seller_id) : null) ||
                (effectiveLead?.seller_id ? Number(effectiveLead.seller_id) : null) ||
                (seller_id && !Number.isNaN(Number(seller_id)) ? Number(seller_id) : null)
            if (resolvedSellerId != null) {
                payload.seller_id = resolvedSellerId
            }
            return sanitizedInputs(payload) as Partial<Lead>
        },
        [selectedCategory, effectiveLead, isOpportunity, tags, seller_id]
    )

    const onFormError = useCallback((errs: unknown) => {
        console.error('Form validation errors:', errs)
        toast.error(t('Please fix the errors in the form'))
    }, [t])

    const onSubmit = useCallback(
        async (data: LeadBasicInputs, shouldClose: boolean = true) => {
            setIsSubmitting(true)
            try {
                let companyToUse = selectedCompany
                let contactToUse = selectedContact
                const resolvedGender = (data.gender ?? genderInput) || undefined
                const selectedBranchType = normalizeCompanyBranchType(data.company_type)

                if (!contactToUse?.id && selectedCompanyContactId) {
                    contactToUse = companyContacts.find((c) => c.id === selectedCompanyContactId) || null
                }

                const normalizedCompanyName = data.company_name?.toString().trim() || ''
                const fullContactName = [data.first_name, data.last_name]
                    .map((value) => value?.toString().trim() || '')
                    .filter(Boolean)
                    .join(' ')
                const fallbackPrivateCompanyName = fullContactName || data.name?.toString().trim() || ''
                const shouldCreatePrivateCompany =
                    selectedBranchType === CompanyBranchType.Private && !selectedCompany?.id
                const companyNameForCreate =
                    normalizedCompanyName || (shouldCreatePrivateCompany ? fallbackPrivateCompanyName : '')

                let addressCreateMode = isCreatingNewAddress
                let activeCompanyAddresses = companyAddresses
                let activeSelectedAddressId = selectedCompanyAddressId

                if (companyNameForCreate && !selectedCompany?.id) {
                    const companyData: Partial<Company> = {
                        name: companyNameForCreate,
                        email: data.email?.toString().trim() || '',
                        phone: data.phone?.toString().trim() || '',
                        billing_info: companyNameForCreate,
                        first_name: data.first_name?.toString().trim() || '',
                        last_name: data.last_name?.toString().trim() || '',
                        type: selectedBranchType,
                        business_category: data.business_category?.toString().trim() || undefined,
                        origin: normalizeCompanyOrigin(data.company_origin) || undefined,
                    }
                    try {
                        const newCompany = await dispatch(
                            CompaniesAction.fetchCreateOrUpdateCompany(companyData as Company)
                        )
                        if (newCompany) {
                            companyToUse = mergeCompanyAfterSave(companyData, newCompany)
                            setSelectedCompany(companyToUse)
                            const createdAddresses = newCompany.addresses ?? []
                            if (createdAddresses.length > 0) {
                                setCompanyAddresses(createdAddresses)
                                setSelectedCompanyAddressId(
                                    newCompany.default_address_id ?? createdAddresses[0].id
                                )
                                setIsCreatingNewAddress(false)
                                addressCreateMode = false
                                activeCompanyAddresses = createdAddresses
                                activeSelectedAddressId =
                                    newCompany.default_address_id ?? createdAddresses[0].id
                            }
                            toast.success(t('Company created successfully'))
                        }
                    } catch (err) {
                        toast.error(t('Failed to create company'))
                        throw err
                    }
                }

                const hasStreetAddress = Boolean(data.street_address?.trim())
                const hasCity = Boolean(data.city?.trim())
                const hasPostalCode = Boolean(data.postal_code?.trim())
                const hasState = Boolean(data.state?.trim())
                const hasCountry = Boolean(data.country?.trim())
                const hasAnyAddressInput = Boolean(
                    hasStreetAddress || hasCity || hasPostalCode || hasState || hasCountry
                )
                const hasAllRequiredAddressFields = Boolean(
                    hasStreetAddress && hasCity && hasPostalCode && hasState && hasCountry
                )
                const companyHasAddresses = Boolean(companyToUse?.addresses && companyToUse.addresses.length > 0)
                // City/region autofill alone is not enough to create/update an address — require a complete set.
                const shouldCreateAddress =
                    hasAllRequiredAddressFields && (addressCreateMode || !companyHasAddresses)
                const shouldUpdateAddress =
                    hasAllRequiredAddressFields && !shouldCreateAddress && companyHasAddresses
                const requiresExistingAddressSelection =
                    hasAllRequiredAddressFields &&
                    !shouldCreateAddress &&
                    companyHasAddresses &&
                    activeSelectedAddressId == null
                const incompleteAddressAttempt =
                    companyToUse?.id && hasAnyAddressInput && !hasAllRequiredAddressFields

                if (requiresExistingAddressSelection) {
                    toast.error(t('Please select an existing address or click New address'))
                    setIsSubmitting(false)
                    return
                }

                const selectedAddressToUpdate =
                    activeSelectedAddressId == null
                        ? null
                        : activeCompanyAddresses.find((address) => address.id === activeSelectedAddressId) ||
                          companyToUse?.addresses?.find((address) => address.id === activeSelectedAddressId) ||
                          null

                if (incompleteAddressAttempt) {
                    const requiredMessage = t('This field is required')
                    if (!hasStreetAddress) {
                        setError('street_address', {type: 'manual', message: requiredMessage})
                    }
                    if (!hasPostalCode) {
                        setError('postal_code', {type: 'manual', message: requiredMessage})
                    }
                    if (!hasCity) {
                        setError('city', {type: 'manual', message: requiredMessage})
                    }
                    if (!hasState) {
                        setError('state', {type: 'manual', message: requiredMessage})
                    }
                    if (!hasCountry) {
                        setError('country', {type: 'manual', message: requiredMessage})
                    }

                    // Explicit "New address" or street/postal intent: block save until complete.
                    // Locality-only autofill (city/region/country) must not block the whole lead save.
                    const hasExplicitAddressIntent = addressCreateMode || hasStreetAddress || hasPostalCode
                    if (hasExplicitAddressIntent) {
                        toast.error(t('Please fill all address fields'))
                        setIsSubmitting(false)
                        return
                    }

                    toast.warning(
                        t('Address was incomplete and was not saved. Fill street and postal code to save an address.')
                    )
                }

                if (companyToUse?.id && shouldCreateAddress) {
                    const normalizedCountry = await getEnglishCountryNameFromAnyCountryValue(
                        data.country?.toString().trim() || 'Switzerland',
                        'Switzerland'
                    )
                    try {
                        const createdAddress = await dispatch(
                            CompaniesAction.fetchCreateOrUpdateAddress({
                                name: 'company',
                                department_name: '',
                                street_name: data.street_address?.toString().trim() || '',
                                postal_code: data.postal_code?.toString().trim() || '',
                                town: data.city?.toString().trim() || '',
                                region: data.state?.toString().trim() || '',
                                country: normalizedCountry,
                                note: '',
                                company_id: companyToUse.id,
                            } as CompanyAddress)
                        )
                        if (createdAddress) {
                            setCompanyAddresses((prev) => {
                                const withoutSameId = prev.filter((a) => a.id !== createdAddress.id)
                                return [...withoutSameId, createdAddress]
                            })
                            setSelectedCompanyAddressId(createdAddress.id)
                            setIsCreatingNewAddress(false)
                            setSelectedCompany({
                                ...companyToUse!,
                                addresses: [
                                    ...((companyToUse!.addresses || []).filter((a) => a.id !== createdAddress.id)),
                                    createdAddress,
                                ],
                                default_address_id: createdAddress.id,
                            })
                        }
                    } catch {
                        toast.error(t('Failed to create company address'))
                    }
                }

                if (
                    companyToUse?.id &&
                    shouldUpdateAddress &&
                    selectedAddressToUpdate
                ) {
                    const normalizedCountry = await getEnglishCountryNameFromAnyCountryValue(
                        data.country?.toString().trim() || selectedAddressToUpdate.country || 'Switzerland',
                        'Switzerland'
                    )
                    const updatedAddressPayload: CompanyAddress = {
                        ...selectedAddressToUpdate,
                        id: selectedAddressToUpdate.id,
                        company_id: companyToUse.id,
                        name: selectedAddressToUpdate.name || 'company',
                        department_name: selectedAddressToUpdate.department_name || '',
                        street_name: data.street_address?.toString().trim() || '',
                        postal_code: data.postal_code?.toString().trim() || '',
                        town: data.city?.toString().trim() || '',
                        region: data.state?.toString().trim() || '',
                        country: normalizedCountry,
                        note: selectedAddressToUpdate.note || '',
                    }
                    const hasAddressChanges =
                        (selectedAddressToUpdate.street_name || '') !== updatedAddressPayload.street_name ||
                        (selectedAddressToUpdate.postal_code || '') !== updatedAddressPayload.postal_code ||
                        (selectedAddressToUpdate.town || '') !== updatedAddressPayload.town ||
                        (selectedAddressToUpdate.region || '') !== updatedAddressPayload.region ||
                        (selectedAddressToUpdate.country || '') !== updatedAddressPayload.country
                    if (hasAddressChanges) {
                        try {
                            const updatedAddress = await dispatch(
                                CompaniesAction.fetchCreateOrUpdateAddress(updatedAddressPayload)
                            )
                            if (updatedAddress) {
                                setCompanyAddresses((prev) =>
                                    prev.map((a) => (a.id === updatedAddress.id ? updatedAddress : a))
                                )
                                setSelectedCompany((prev) =>
                                    prev
                                        ? {
                                              ...prev,
                                              addresses: (prev.addresses || []).map((a) =>
                                                  a.id === updatedAddress.id ? updatedAddress : a
                                              ),
                                          }
                                        : prev
                                )
                            }
                        } catch {
                            toast.error(t('Failed to update company address'))
                        }
                    }
                }

                if (companyToUse?.id && shouldUpdateAddress && !selectedAddressToUpdate) {
                    toast.error(t('Selected company address no longer exists. Please re-select the address.'))
                    setIsSubmitting(false)
                    return
                }

                if ((data.first_name || data.last_name) && !contactToUse?.id) {
                    const contactData: Partial<CompanyContact> = {
                        company_id: companyToUse?.id || null,
                        title: data.title?.toString().trim() || '',
                        custom_title: data.custom_title?.toString().trim() || '',
                        position: data.position?.toString().trim() || '',
                        first_name: data.first_name?.toString().trim() || '',
                        last_name: data.last_name?.toString().trim() || '',
                        // Contact must keep its own email/phone — never fall back to Account fields.
                        email: data.contact_email?.toString().trim() || '',
                        phone: data.contact_phone?.toString().trim() || '',
                        gender: resolvedGender,
                        origin: normalizeCompanyOrigin(data.contact_origin) || '',
                        note: data.contact_note?.toString().trim() || '',
                    }
                    try {
                        const newContact = await dispatch(
                            CompaniesAction.fetchCreateOrUpdateContact(contactData as CompanyContact)
                        )
                        if (newContact) {
                            contactToUse = newContact
                            setSelectedContact(newContact)
                        }
                    } catch (err) {
                        console.error('Failed to create contact', err)
                    }
                }

                if (contactToUse?.id) {
                    const normalizedTitle = data.title?.toString().trim() || ''
                    const normalizedCustomTitle = data.custom_title?.toString().trim() || ''
                    const normalizedPosition = data.position?.toString().trim() || ''
                    const normalizedFirstName = data.first_name?.toString().trim() || ''
                    const normalizedLastName = data.last_name?.toString().trim() || ''
                    const normalizedContactEmail = data.contact_email?.toString().trim() || ''
                    const normalizedContactPhone = data.contact_phone?.toString().trim() || ''
                    const normalizedGender = resolvedGender
                    const normalizedOrigin = normalizeCompanyOrigin(data.contact_origin) || ''
                    const normalizedNote = data.contact_note?.toString().trim() || ''

                    const hasContactChanges =
                        (contactToUse.title || '') !== normalizedTitle ||
                        (contactToUse.custom_title || '') !== normalizedCustomTitle ||
                        (contactToUse.position || '') !== normalizedPosition ||
                        (contactToUse.first_name || '') !== normalizedFirstName ||
                        (contactToUse.last_name || '') !== normalizedLastName ||
                        (contactToUse.email || '') !== normalizedContactEmail ||
                        (contactToUse.phone || '') !== normalizedContactPhone ||
                        (contactToUse.gender || undefined) !== normalizedGender ||
                        (contactToUse.origin || '') !== normalizedOrigin ||
                        (contactToUse.note || '') !== normalizedNote ||
                        (contactToUse.company_id || null) !== (companyToUse?.id || null)

                    if (hasContactChanges) {
                        try {
                            const updatedContact = await dispatch(
                                CompaniesAction.fetchCreateOrUpdateContact({
                                    ...contactToUse,
                                    id: contactToUse.id,
                                    company_id: companyToUse?.id || contactToUse.company_id || null,
                                    title: normalizedTitle,
                                    custom_title: normalizedCustomTitle,
                                    position: normalizedPosition,
                                    first_name: normalizedFirstName,
                                    last_name: normalizedLastName,
                                    email: normalizedContactEmail,
                                    phone: normalizedContactPhone,
                                    gender: normalizedGender,
                                    origin: normalizedOrigin,
                                    note: normalizedNote,
                                } as CompanyContact)
                            )
                            if (updatedContact) {
                                const mergedContact = mergeContactAfterSave(
                                    {
                                        position: normalizedPosition,
                                        origin: normalizedOrigin,
                                        title: normalizedTitle,
                                        custom_title: normalizedCustomTitle,
                                        gender: normalizedGender,
                                        note: normalizedNote,
                                    },
                                    updatedContact
                                )
                                contactToUse = mergedContact
                                setSelectedContact(mergedContact)
                                setSelectedCompanyContactId(mergedContact.id)
                                setCompanyContacts((prev) =>
                                    prev.some((c) => c.id === mergedContact.id)
                                        ? prev.map((c) => (c.id === mergedContact.id ? mergedContact : c))
                                        : [...prev, mergedContact]
                                )
                                setValue('position', mergedContact.position || normalizedPosition, {
                                    shouldDirty: false,
                                })
                            }
                        } catch (err) {
                            console.error('Failed to update contact', err)
                            toast.error(t('Failed to update contact'))
                            setIsSubmitting(false)
                            return
                        }
                    }
                }

                const payload = getSubmitPayload(data)
                // Branch / Branche / origin live on Company — sync from oppo/lead Detail form on save.
                if (!companyToUse?.id && effectiveLead?.company?.id) {
                    companyToUse = effectiveLead.company
                }
                if (companyToUse?.id) {
                    payload.company_id = companyToUse.id
                    payload.company_name = companyToUse.name || payload.company_name
                    const newBusinessCategory = data.business_category?.toString().trim() || null
                    const newCompanyType = normalizeCompanyBranchType(data.company_type)
                    const newCompanyOrigin = normalizeCompanyOrigin(data.company_origin) || null
                    const existingCompanyType =
                        companyToUse.type != null && String(companyToUse.type).trim() !== ''
                            ? normalizeCompanyBranchType(companyToUse.type)
                            : null
                    const businessCategoryChanged =
                        newBusinessCategory !== (companyToUse.business_category || null)
                    const companyTypeChanged = newCompanyType !== existingCompanyType
                    const companyOriginChanged = newCompanyOrigin !== (companyToUse.origin || null)
                    if (businessCategoryChanged || companyTypeChanged || companyOriginChanged) {
                        try {
                            // Minimal payload (same shape as CompanyBasicForm) — do not re-post API
                            // response scalars like is_vat_payer: 1 (backend expects string).
                            const companyUpdatePayload = pruneEmpty(
                                {
                                    id: companyToUse.id,
                                    name: companyToUse.name,
                                    billing_info: companyToUse.billing_info || companyToUse.name,
                                    type: newCompanyType,
                                    business_category: newBusinessCategory,
                                    origin: newCompanyOrigin,
                                },
                                ['business_category', 'origin']
                            ) as unknown as Company
                            const updatedCompany = await dispatch(
                                CompaniesAction.fetchCreateOrUpdateCompany(companyUpdatePayload)
                            )
                            if (updatedCompany) {
                                companyToUse = mergeCompanyAfterSave(
                                    {
                                        type: newCompanyType,
                                        business_category: newBusinessCategory,
                                        origin: newCompanyOrigin,
                                    },
                                    updatedCompany
                                )
                                setSelectedCompany(companyToUse)
                            }
                        } catch (err) {
                            console.error('Failed to sync account branch fields from lead form', err)
                            toast.error(t('Failed to save company information'))
                        }
                    }
                }

                payload.contact_id = contactToUse?.id ?? null
                if (!companyToUse?.id) payload.company_id = null
                if (!payload.company_name && !companyToUse?.id) payload.company_name = null

                const response: Lead = await dispatch(action.fetchCreateOrUpdateLead(payload))
                if (response) {
                    const mergedLead = mergeLeadAfterSave(
                        {
                            created_date:
                                typeof payload.created_date === 'string' ? payload.created_date : null,
                            closed_at: typeof payload.closed_at === 'string' ? payload.closed_at : null,
                        },
                        {
                            ...response,
                            company: response.company
                                ? mergeCompanyAfterSave(
                                      {
                                          origin: companyToUse?.origin ?? null,
                                          business_category: companyToUse?.business_category ?? null,
                                          type: companyToUse?.type,
                                      },
                                      {
                                          ...response.company,
                                          ...(companyToUse?.id === response.company.id
                                              ? {
                                                    origin:
                                                        response.company.origin ||
                                                        companyToUse.origin ||
                                                        null,
                                                }
                                              : {}),
                                      }
                                  )
                                : companyToUse
                                  ? companyToUse
                                  : response.company,
                        }
                    )
                    dispatch(action.setSelectedLead(mergedLead))
                    dispatch(action.pushLead(mergedLead))

                    const assignedSellerId = payload.seller_id != null ? Number(payload.seller_id) : null
                    if (
                        assignedSellerId != null &&
                        assignedSellerId !== Number(effectiveLead?.seller_id ?? NaN)
                    ) {
                        await createAssignmentReminderForEntity(
                            dispatch as unknown as AppDispatch,
                            {
                                // Opportunities are Lead records on the backend.
                                modelName: ReminderModelName.Lead,
                                modelId: Number(mergedLead.id),
                                sellerId: assignedSellerId,
                                previousSellerId: effectiveLead?.seller_id,
                                hallId: mergedLead.hall_id ?? payload.hall_id,
                                content: t('Assigned to you') as string,
                            }
                        )
                    }

                    toast.success(t('Lead saved successfully'))

                    if (effectiveLead?.id) {
                        const newContactId = contactToUse?.id ?? null
                        const originalContactId = effectiveLead?.contact_id ?? null
                        const contactChanged = originalContactId !== newContactId
                        const addressChanged =
                            addressExplicitlyChangedRef.current && selectedCompanyAddressId != null
                        const offersToSync = (effectiveLead?.offers ?? []).filter((offer) => {
                            if (contactChanged && offer.contact_id !== newContactId) return true
                            if (addressChanged && offer.address_id !== selectedCompanyAddressId) return true
                            return false
                        })
                        if (offersToSync.length > 0) {
                            dispatch(
                                openSecondaryModal(
                                    <UiAlertConfirmationModal
                                        title={t('Update Offers')}
                                        message={t(
                                            'This change will also be applied to {{count}} linked offer(s). Do you want to continue?',
                                            {count: offersToSync.length}
                                        )}
                                        onConfirm={async () => {
                                            dispatch(closeSecondaryModal())
                                            for (const offer of offersToSync) {
                                                const offerUpdatePayload: Record<string, unknown> = {
                                                    id: offer.id,
                                                    seller_id: offer.seller_id,
                                                }
                                                if (contactChanged && offer.contact_id !== newContactId) {
                                                    offerUpdatePayload.contact_id = newContactId
                                                }
                                                if (
                                                    addressChanged &&
                                                    offer.address_id !== selectedCompanyAddressId
                                                ) {
                                                    offerUpdatePayload.address_id = selectedCompanyAddressId
                                                }
                                                try {
                                                    await dispatch(
                                                        OffersAction.updateOffer(
                                                            offerUpdatePayload as Parameters<typeof OffersAction.updateOffer>[0]
                                                        )
                                                    )
                                                } catch (e) {
                                                    console.error('Failed to sync offer contact/address:', e)
                                                }
                                            }
                                        }}
                                        onCancel={() => dispatch(closeSecondaryModal())}
                                    />,
                                    {modalSize: ModalSize.Small},
                                    t('Update Offers')
                                )
                            )
                        }
                    }
                    if (shouldClose) {
                        reset()
                        setSelectedCategory(undefined)
                    }
                    onSuccess(mergedLead, shouldClose)
                }
            } catch (e: unknown) {
                let msg = t('Failed to save lead')
                const err = e as Record<string, unknown>
                const messages = (err?.data as Record<string, unknown>)?.messages ??
                    ((err?.response as Record<string, unknown>)?.data &&
                        (err?.response as Record<string, Record<string, unknown>>)?.data?.messages) ??
                    err?.messages
                if (messages && typeof messages === 'object') {
                    const firstKey = Object.keys(messages as object)[0]
                    const firstVal = (messages as Record<string, unknown[]>)[firstKey]
                    if (Array.isArray(firstVal) && firstVal.length > 0) {
                        msg = firstVal[0] as string
                    }
                } else if (typeof err?.message === 'string') {
                    msg = err.message
                }
                toast.error(msg)
            } finally {
                setIsSubmitting(false)
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            selectedCompany,
            selectedContact,
            genderInput,
            selectedCompanyContactId,
            companyContacts,
            companyAddresses,
            selectedCompanyAddressId,
            isCreatingNewAddress,
            getSubmitPayload,
            action,
            effectiveLead,
            dispatch,
            reset,
            onSuccess,
            t,
        ]
    )

    const resolveCountryCode = useCallback(
        (countryValue?: string) => {
            const match = findCountryOption(countryValue)
            return match?.textCode || 'CH'
        },
        [findCountryOption]
    )
    const [selectedCountryCode, setSelectedCountryCode] = useState<string>(
        resolveCountryCode(countryInput?.toString())
    )

    const normalizedCountryInput = (countryInput || '')
        .toString()
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
    const isSwissCountryInput = ['switzerland', 'schweiz', 'suisse'].includes(normalizedCountryInput)
    const hasAddressField =
        (streetAddressInput && streetAddressInput.toString().trim() !== '') ||
        (postalCodeInput && postalCodeInput.toString().trim() !== '') ||
        (cityInput && cityInput.toString().trim() !== '')

    const hasExistingCompanyAddresses = Boolean(selectedCompany?.id && companyAddresses.length > 0)
    const selectedCompanyContact =
        selectedContact || companyContacts.find((c) => c.id === selectedCompanyContactId) || null
    const companyAddressOptions = useMemo(
        () =>
            companyAddresses.map((address) => ({
                value: address.id,
                label: formatAddressSummary(address.street_name, address.postal_code, address.town),
                description: [address.region, address.country].filter(Boolean).join(', '),
            })),
        [companyAddresses]
    )

    // Effects
    useEffect(() => {
        const countryValue = (countryInput || '').toString()
        const match = findCountryOption(countryValue)
        if (match?.textCode) {
            setSelectedCountryCode(match.textCode)
        }
    }, [countryInput, findCountryOption])

    useEffect(() => {
        if (getValues('is_new_contact') !== isNewContact) {
            setValue('is_new_contact', isNewContact, {shouldValidate: true})
        }
    }, [isNewContact, setValue, getValues])

    useEffect(() => {
        onRequirementsChange?.({hasCompany, hasContact})
    }, [hasCompany, hasContact, onRequirementsChange])

    useEffect(() => {
        if (!shouldCheckDuplicatesRef.current) return
        checkDuplicates(undefined, companyNameInput || undefined, emailInput || undefined)
    }, [companyNameInput, emailInput, checkDuplicates])

    useEffect(() => {
        if (!accountTypeAutoCategoryMountedRef.current) {
            accountTypeAutoCategoryMountedRef.current = true
            return
        }

        const autoCategory = getAutoBusinessCategoryForBranchType(companyTypeInput)
        if (autoCategory) {
            setValue('business_category', autoCategory, {shouldValidate: false, shouldDirty: true})
        }
    }, [companyTypeInput, setValue])

    useEffect(() => {
        if (effectiveLead) {
            const effectiveLeadCompany = effectiveLead.company || null
            const localSelectedCompany = selectedCompany || null
            const fallbackCompany = company || null

            const getCompanyAddressScore = (value: Company | null) => {
                if (!value) return 0
                const addressesScore = (value.addresses?.length || 0) * 100
                const scalarScore = [value.street_address, value.city, value.postal_code, value.state, value.country]
                    .filter((field) => typeof field === 'string' && field.trim() !== '').length
                return addressesScore + scalarScore
            }

            let companyToUse: Company | null = null
            if (effectiveLeadCompany?.id) {
                if (localSelectedCompany?.id === effectiveLeadCompany.id) {
                    const preferredByAddress =
                        getCompanyAddressScore(localSelectedCompany) >= getCompanyAddressScore(effectiveLeadCompany)
                            ? localSelectedCompany
                            : effectiveLeadCompany
                    companyToUse = mergeCompanyAfterSave(
                        {
                            origin: localSelectedCompany.origin || effectiveLeadCompany.origin,
                            business_category:
                                localSelectedCompany.business_category ||
                                effectiveLeadCompany.business_category,
                            type: localSelectedCompany.type || effectiveLeadCompany.type,
                        },
                        preferredByAddress
                    )
                } else {
                    companyToUse = effectiveLeadCompany
                }
            } else if (localSelectedCompany) {
                companyToUse = localSelectedCompany
            } else {
                companyToUse = fallbackCompany
            }

            const companyForAddressResolve: Company | null = companyToUse
                ? {
                      ...companyToUse,
                      addresses:
                          companyAddresses.length > 0 && companyToUse.id === localSelectedCompany?.id
                              ? companyAddresses
                              : companyToUse.addresses,
                  }
                : null
            const defaultAddressId =
                selectedCompanyAddressId ?? companyToUse?.default_address_id ?? null
            const defaultCompanyAddress =
                companyForAddressResolve?.addresses?.find((address) => address.id === defaultAddressId) ||
                companyForAddressResolve?.addresses?.[0]
            const resolvedAddressFields = getLeadFormAddressFields(
                companyForAddressResolve,
                defaultAddressId
            )
            const currentValues = getValues()
            const resolveAddressFieldOnReset = (
                field: keyof LeadFormAddressFields,
                serverValue: string,
                currentValue?: string
            ): string => {
                if (dirtyFields[field]) {
                    return (currentValue ?? serverValue) || ''
                }
                return serverValue || currentValue || ''
            }
            const contactForReset =
                effectiveLead.contact_id && selectedContact?.id === effectiveLead.contact_id
                    ? selectedContact
                    : effectiveLead.contact ??
                      (selectedContactRef.current?.id ? selectedContactRef.current : null)
            shouldCheckDuplicatesRef.current = false
            const resetAnlasstyp = effectiveLead?.anlasstyp || ''
            const resetCompanyName = effectiveLead?.company_name || companyToUse?.name || ''
            const resetName = effectiveLead?.name || ''
            const seededAutoName = buildOpportunityName(resetAnlasstyp, resetCompanyName)
            lastAutoOpportunityNameRef.current =
                seededAutoName && resetName.trim() === seededAutoName ? seededAutoName : ''
            reset(
                applyLeadEventFormDefaults(effectiveLead, {
                name: resetName,
                company_name: resetCompanyName,
                anlasstyp: resetAnlasstyp,
                first_name:
                    contactForReset?.first_name ||
                    (dirtyFields.first_name ? currentValues.first_name : '') ||
                    '',
                title:
                    contactForReset?.title ||
                    (dirtyFields.title ? currentValues.title : '') ||
                    '',
                custom_title:
                    contactForReset?.custom_title ||
                    (dirtyFields.custom_title ? currentValues.custom_title : '') ||
                    '',
                position:
                    contactForReset?.position ||
                    (dirtyFields.position ? currentValues.position : '') ||
                    '',
                last_name:
                    contactForReset?.last_name ||
                    (dirtyFields.last_name ? currentValues.last_name : '') ||
                    '',
                gender:
                    contactForReset?.gender ||
                    (dirtyFields.gender ? currentValues.gender : undefined) ||
                    undefined,
                contact_origin:
                    contactForReset?.origin ||
                    (dirtyFields.contact_origin ? currentValues.contact_origin : '') ||
                    '',
                contact_note:
                    contactForReset?.note ||
                    (dirtyFields.contact_note ? currentValues.contact_note : '') ||
                    '',
                company_type: normalizeCompanyBranchType(companyToUse?.type),
                business_category: companyToUse?.business_category || '',
                company_origin:
                    companyToUse?.origin ||
                    (dirtyFields.company_origin ? currentValues.company_origin : '') ||
                    '',
                email: effectiveLead?.email || '',
                phone: effectiveLead?.phone || '',
                contact_email:
                    contactForReset?.email ||
                    effectiveLead?.contact?.email ||
                    (dirtyFields.contact_email ? currentValues.contact_email : '') ||
                    effectiveLead?.email ||
                    '',
                contact_phone:
                    contactForReset?.phone ||
                    effectiveLead?.contact?.phone ||
                    (dirtyFields.contact_phone ? currentValues.contact_phone : '') ||
                    effectiveLead?.phone ||
                    '',
                website: effectiveLead?.website || '',
                source: effectiveLead?.source || '',
                referral_id:
                    effectiveLead?.referral_id != null
                        ? String(effectiveLead.referral_id)
                        : companyToUse?.referral_id != null
                          ? String(companyToUse.referral_id)
                          : '',
                tags: Array.isArray(effectiveLead?.tags)
                    ? effectiveLead.tags.filter((tag: string) => typeof tag === 'string' && tag.trim() !== '')
                    : [],
                street_address: resolveAddressFieldOnReset(
                    'street_address',
                    resolvedAddressFields.street_address,
                    currentValues.street_address
                ),
                city: resolveAddressFieldOnReset('city', resolvedAddressFields.city, currentValues.city),
                postal_code: resolveAddressFieldOnReset(
                    'postal_code',
                    resolvedAddressFields.postal_code,
                    currentValues.postal_code
                ),
                state: resolveAddressFieldOnReset('state', resolvedAddressFields.state, currentValues.state),
                country: resolveAddressFieldOnReset(
                    'country',
                    resolvedAddressFields.country,
                    currentValues.country
                ),
                persons: effectiveLead?.persons || 0,
                seller_id: effectiveLead?.seller_id || undefined,
                requested_date: (() => {
                    if (!effectiveLead?.requested_date) return undefined
                    let date = dayjs(effectiveLead.requested_date)
                    if (effectiveLead.requested_time) {
                        const [hours, minutes] = effectiveLead.requested_time.split(':')
                        date = date.hour(parseInt(hours, 10)).minute(parseInt(minutes, 10))
                    }
                    return date.toDate()
                })(),
                created_date:
                    parseLeadDateField(effectiveLead?.created_date) ??
                    (dirtyFields.created_date ? currentValues.created_date : undefined) ??
                    (isOpportunity && !effectiveLead?.created_date && !effectiveLead?.id
                        ? getDefaultOpportunityCreatedDate()
                        : undefined),
                closed_at: parseLeadDateField(effectiveLead?.closed_at),
                is_new_contact: false,
                })
            )
            setSelectedCategory(effectiveLead.category)
            setTags(
                Array.isArray(effectiveLead.tags)
                    ? effectiveLead.tags.filter((tag: string) => typeof tag === 'string' && tag.trim() !== '')
                    : []
            )
            setSelectedCompany(companyToUse)
            setCompanyContacts(companyToUse?.contacts || [])
            setCompanyAddresses(companyToUse?.addresses || [])
            setSelectedCompanyAddressId(defaultCompanyAddress?.id || null)
            setCompanyInputValue(effectiveLead?.company_name || companyToUse?.name || '')
            if (effectiveLead.contact) {
                setSelectedContact(effectiveLead.contact)
                setSelectedCompanyContactId(effectiveLead.contact.id)
            } else if (effectiveLead.contact_id) {
                setSelectedCompanyContactId(effectiveLead.contact_id)
                if (selectedContactRef.current?.id === effectiveLead.contact_id) {
                    // Keep the already-loaded contact entity.
                } else {
                    dispatch(ContactsAction.fetchContactById(effectiveLead.contact_id))
                        .then((contact) => setSelectedContact(contact))
                        .catch(() => setSelectedContact(null))
                }
            } else if (selectedContactRef.current?.id) {
                // User selected a contact that is not yet persisted on the lead — do not wipe it
                // when this effect re-runs for unrelated callback identity changes.
                setSelectedCompanyContactId(selectedContactRef.current.id)
            } else {
                setSelectedContact(null)
                setSelectedCompanyContactId(
                    companyToUse?.default_contact_id || companyToUse?.contacts?.[0]?.id || null
                )
            }
        } else {
            shouldCheckDuplicatesRef.current = false
            lastAutoOpportunityNameRef.current = ''
            reset({
                name: '',
                company_name: '',
                anlasstyp: '',
                first_name: '',
                title: '',
                custom_title: '',
                position: '',
                last_name: '',
                gender: undefined,
                contact_origin: '',
                contact_note: '',
                email: '',
                phone: '',
                contact_email: '',
                contact_phone: '',
                website: '',
                source: '',
                referral_id: '',
                company_type: CompanyBranchType.Company,
                business_category: '',
                company_origin: '',
                requested_date: undefined,
                created_date: isOpportunity ? getDefaultOpportunityCreatedDate() : undefined,
                closed_at: undefined,
                persons: 0,
                seller_id: seller_id || undefined,
                tags: [],
                street_address: '',
                city: '',
                postal_code: '',
                state: '',
                country: 'Switzerland',
                is_new_contact: false,
            })
            setSelectedCategory(undefined)
            setTags([])
            setSelectedCompany(null)
            setCompanyContacts([])
            setSelectedCompanyContactId(null)
            setCompanyAddresses([])
            setSelectedCompanyAddressId(null)
            setCompanyInputValue('')
            if (predefinedContact) handleContactSelect(predefinedContact)
            if (company) handleCompanySelect(company, false)
        }
        // Only re-sync when the lead identity/relations change — not when select handlers are recreated.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadFormSyncToken, company?.id, predefinedContact?.id, seller_id])

    return {
        // Form
        form,
        register,
        handleSubmit,
        control,
        errors,
        setValue,
        watch,
        // Submit
        onSubmit,
        onFormError,
        isSubmitting,
        // Lead context
        isOpportunity,
        testField,
        effectiveLead,
        // Company
        selectedCompany,
        companyInputValue,
        companyAutocompleteKey,
        companyContacts,
        companyTypeOptions: companyTypeOptionsUi,
        businessCategoryOptions,
        handleCompanySelect,
        handleCompanyNameChange,
        handleCreateCompanyFromLead,
        handleAutoSaveBlur,
        // Contact
        selectedContact,
        selectedCompanyContact,
        selectedContactCountry,
        setSelectedContactCountry,
        handleContactSelect,
        isNewContact,
        titleInput,
        contactPhoneInput,
        // Address
        companyAddresses,
        selectedCompanyAddressId,
        companyAddressOptions,
        hasExistingCompanyAddresses,
        isCreatingNewAddress,
        handleCompanyAddressSelect,
        handleCreateNewAddress,
        orderedCountryOptions,
        findCountryOption,
        selectedCountryCode,
        setSelectedCountryCode,
        countryInput,
        isSwissCountryInput,
        hasAddressField,
        // Tags
        tags,
        setTags,
    }
}
